import { AxiosError } from 'axios';
import Auction from '~/App/shared/interfaces/Auction';
import BaseObject from '~/App/shared/interfaces/BaseObject';
import {
  NotificationSettings,
  NotificationType
} from '~/App/shared/interfaces/NotificationSettings';
import { PayoutDetails } from '~/App/shared/interfaces/PayoutDetails';
import ProcessObject from '~/App/shared/interfaces/ProcessObject';
import { SaleStatus } from '~/App/shared/interfaces/SaleStatus';
import { SavedSearch } from '~/App/shared/interfaces/SavedSearch';
import {
  BusinessProposal,
  SalesAgreement
} from '~/App/shared/interfaces/store/SellFlow';
import { orchestration } from '~/config/public';
import axios from '~/helpers/orchestration/axiosWithAuthHandling';
import { getGaClientId } from '../client/cookie';

export const getBusinessProposals = ({
  idToken: _idToken
}: {
  idToken: string;
}) =>
  axios
    .get<{ businessProposals: BusinessProposal[] }>(
      `${orchestration.url}/mypages/business-proposals/me?limit=100`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.businessProposals);

export const abortBusinessProposal = ({
  idToken: _idToken,
  proposalId
}: {
  idToken: string;
  proposalId: string;
}) =>
  axios
    .put<{ baseObject: BaseObject }>(
      `${orchestration.url}/mypages/base-objects/${proposalId}/abort`,
      {},
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.baseObject);

export const getProcessObjects = ({ idToken: _idToken }: { idToken: string }) =>
  axios
    .get<{ processObjects: ProcessObject[] }>(
      `${orchestration.url}/mypages/process-objects/me?limit=100`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.processObjects);

interface ProcessObjectPayoutDetails extends ProcessObject {
  payoutDetails: PayoutDetails;
}

export const updateMyPayoutDetails = ({
  idToken: _idToken,
  processObjectId,
  payoutDetails
}: {
  idToken: string;
  processObjectId: string;
  payoutDetails: Partial<PayoutDetails> | null;
}) =>
  axios
    .put<{ processObject: ProcessObjectPayoutDetails }>(
      `${orchestration.url}/mypages/process-objects/me/${processObjectId}`,
      {
        processObject: {
          payoutDetails
        }
      },
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.processObject);

export type KvdDocument = {
  key: string;
  text: string;
  url: string;
};

export type GetMySaleResponse = {
  baseObject: BaseObject;
  businessProposalToken: string;
  documents: KvdDocument[];
  processObject: ProcessObject;
  salesStatuses: SaleStatus[];
  salesAgreement: SalesAgreement;
};

export const getMySale = ({
  idToken: _idToken,
  processObjectId
}: {
  idToken: string;
  processObjectId: string;
}) =>
  axios
    .get<GetMySaleResponse>(
      `${orchestration.url}/mypages/sales/${processObjectId}`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data);

export const getMySavedSearches = ({
  idToken: _idToken
}: {
  idToken: string;
}) =>
  axios
    .get<{ savedSearches: SavedSearch[] }>(
      `${orchestration.url}/mypages/saved-searches`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.savedSearches);

export const getTargetedAuctionsById = ({
  idToken: _idToken,
  targetedAuctionId
}: {
  idToken: string;
  targetedAuctionId: number;
}) =>
  axios
    .get<{ auctions: Auction[] }>(
      `${orchestration.url}/mypages/targeted-auctions/${targetedAuctionId}`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.auctions);

export const getHasSeenLatestAuctionsStatus = ({
  idToken: _idToken,
  groupId,
  memberId
}: {
  idToken: string;
  groupId: number;
  memberId: string;
}) =>
  axios
    .get<{
      groupMember: { groupId: number; hasSeenLatestAuctions: boolean };
    }>(
      `${orchestration.url}/mypages/member-groups/${groupId}/members/${memberId}`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.groupMember);

export const setUserHasSeenLatestAuctions = ({
  idToken: _idToken,
  groupId,
  memberId,
  hasSeen
}: {
  idToken: string;
  groupId: number;
  memberId: string;
  hasSeen: boolean;
}) =>
  axios
    .put<{
      groupMember: { groupId: number; hasSeenLatestAuctions: boolean };
    }>(
      `${orchestration.url}/mypages/member-groups/${groupId}/members/${memberId}`,
      {
        group_member: {
          hasSeenLatestAuctions: hasSeen
        }
      },
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.groupMember);

export const saveSearch = ({
  idToken: _idToken,
  savedSearch
}: {
  idToken: string;
  savedSearch: SavedSearch;
}) =>
  axios
    .post<{ savedSearch: SavedSearch }>(
      `${orchestration.url}/mypages/saved-searches`,
      {
        savedSearch,
        GaClientId: getGaClientId()
      },
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.savedSearch);

export const saveSearchGuest = ({
  email,
  savedSearch,
  addToNewsletter,
  gRecaptchaResponse
}: {
  email: string;
  savedSearch: SavedSearch;
  addToNewsletter?: boolean;
  gRecaptchaResponse: string;
}) =>
  axios
    .post<{ savedSearch: SavedSearch }>(
      `${orchestration.url}/mypages/guest-saved-searches`,
      {
        savedSearch,
        email,
        gRecaptchaResponse,
        emailSubscriptionNames: (addToNewsletter && ['WEEKLY']) || []
      },
      { responseType: 'json' }
    )
    .then(({ data }) => data.savedSearch);

export const editSavedSearch = ({
  idToken: _idToken,
  search
}: {
  idToken: string;
  search: SavedSearch;
}) =>
  axios
    .put<{ savedSearch: SavedSearch }>(
      `${orchestration.url}/mypages/saved-searches/${search.id}`,
      {
        savedSearch: search,
        GaClientId: getGaClientId()
      },
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.savedSearch);

type AcceptHighestBidResponse = {
  auctionId: string;
  evaluationType: string;
  reason: string;
  winningBidId: number;
  winningMemberId: string;
};

export const acceptHighestBid = ({
  idToken: _idToken,
  auctionId
}: {
  idToken: string;
  auctionId: string;
}) =>
  axios
    .put<{ auction: AcceptHighestBidResponse | AxiosError }>(
      `${orchestration.url}/mypages/auctions/${auctionId}/accept-highest-bid`,
      {},
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data);

export const deleteMySavedSearch = ({
  idToken: _idToken,
  searchId
}: {
  idToken: string;
  searchId: string;
}) =>
  axios.delete<{ savedSearch: SavedSearch }>(
    `${orchestration.url}/mypages/saved-searches/${searchId}`,
    {
      data: {
        GaClientId: getGaClientId()
      },
      withCredentials: true,
      responseType: 'json'
    }
  );

export const deleteGuestSavedSearch = ({
  searchId,
  memberId,
  gRecaptchaResponse
}: {
  searchId: string;
  memberId: string;
  gRecaptchaResponse: string;
}) =>
  axios.delete<{ savedSearch: SavedSearch }>(
    `${orchestration.url}/mypages/guest-saved-searches/${searchId}`,
    {
      data: {
        GaClientId: getGaClientId(),
        memberId,
        gRecaptchaResponse
      },
      responseType: 'json'
    }
  );

export const getAccountNotificationSettings = ({
  idToken: _idToken
}: {
  idToken: string;
}) =>
  axios
    .get<{ notificationSettings: NotificationSettings }>(
      `${orchestration.url}/mypages/notification-settings/me`,
      {
        withCredentials: true,
        responseType: 'json'
      }
    )
    .then(({ data }) => data.notificationSettings);

export const saveAccountNotificationSettings = ({
  idToken: _idToken,
  notificationSettings
}: {
  idToken: string;
  notificationSettings: {
    favourites: NotificationType[];
    auctions: NotificationType[];
    savedSearches: NotificationType[];
  };
}) =>
  axios.put<{ notificationSettings: NotificationSettings }>(
    `${orchestration.url}/mypages/notification-settings/me`,
    { notificationSettings: notificationSettings },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );

export const confirmUserSettings = ({
  idToken: _idToken
}: {
  idToken: string;
}) =>
  axios.put<unknown>(
    `${orchestration.url}/mypages/member-settings/me`,
    {
      memberSettings: {
        consentAcquired: true
      }
    },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );

export const saveMemberSettings = ({ language }: { language: string }) =>
  axios.put<unknown>(
    `${orchestration.url}/mypages/member-settings/me`,
    {
      memberSettings: {
        language
      }
    },
    {
      withCredentials: true,
      responseType: 'json'
    }
  );
